import { Button, Flex, Stack, Text } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isTravelAdminRole } from '@/module/Travel/travelRoles';

import { NoContentPlaceholder, NumberFilter, useNumberFilter } from '@/core/components';

import { useCurrentUser } from '@feature/security';
import { FilterBox, useAppNotification } from '@feature/utils';

import { LegalHolidayForm } from './LegalHolidayForm';
import { LegalHolidayRow } from './components/LegalHolidayRow';
import { getLegalHolidayListOptions, useLegalHolidayCreate, useLegalHolidayUpdate } from './model/LegalHolidayQuery';
import {
  LegalHolidaySchema,
  defaultLegalHolidayValues,
  legalHolidaySchema,
  toLegalHolidayRequest,
  toLegalHolidaySchema,
} from './model/LegalHolidaySchema';
import { LegalHoliday } from './model/LegalHolidayTypes';

type Editing = { kind: 'create' } | { kind: 'edit'; legalHoliday: LegalHoliday };

export function LegalHolidayBrowse() {
  const { t } = useTranslation();
  const { currentRole } = useCurrentUser();

  if (!isTravelAdminRole(currentRole)) {
    return <NoContentPlaceholder title={t('legalHolidays.noAccess')} />;
  }

  return <LegalHolidayList />;
}

function LegalHolidayList() {
  const { t } = useTranslation();
  const { item: year, ...yearFilterProps } = useNumberFilter(new Date().getFullYear());
  // A half-typed year debounces to NaN, which would still be sent.
  const { data } = useSuspenseQuery(
    getLegalHolidayListOptions(year != null && Number.isFinite(year) ? year : undefined),
  );
  const [editing, setEditing] = useState<Editing | null>(null);
  // The list shows one year, so a holiday saved for another year would vanish behind its own success toast.
  const showSaved = (saved: LegalHoliday) => yearFilterProps.onChange(Number(saved.date.slice(0, 4)));

  return (
    <Stack>
      <Flex
        justify="space-between"
        align="center"
      >
        <Text
          component="h1"
          size="lg"
          fw="500"
        >
          {t('legalHolidays.title')}
        </Text>
        <Button
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={() => setEditing({ kind: 'create' })}
        >
          {t('legalHolidays.add')}
        </Button>
      </Flex>

      <FilterBox>
        <NumberFilter
          label={t('legalHolidays.filters.year')}
          allowDecimal={false}
          min={1997}
          {...yearFilterProps}
          flex={0.25}
        />
      </FilterBox>

      {data.length === 0 ? (
        <NoContentPlaceholder title={t('legalHolidays.empty')} />
      ) : (
        <Stack gap="xs">
          {data.map(legalHoliday => (
            <LegalHolidayRow
              key={legalHoliday.id}
              legalHoliday={legalHoliday}
              onEdit={item => setEditing({ kind: 'edit', legalHoliday: item })}
            />
          ))}
        </Stack>
      )}

      {editing?.kind === 'create' && (
        <LegalHolidayCreate
          onSaved={showSaved}
          onClose={() => setEditing(null)}
        />
      )}
      {editing?.kind === 'edit' && (
        <LegalHolidayEdit
          legalHoliday={editing.legalHoliday}
          onSaved={showSaved}
          onClose={() => setEditing(null)}
        />
      )}
    </Stack>
  );
}

interface LegalHolidayCreateProps {
  onSaved: (saved: LegalHoliday) => void;
  onClose: () => void;
}

function LegalHolidayCreate({ onSaved, onClose }: LegalHolidayCreateProps) {
  const { t } = useTranslation();
  const notification = useAppNotification();
  const { mutate, isPending, isError, isSuccess } = useLegalHolidayCreate();

  const onSubmit = (values: LegalHolidaySchema) =>
    mutate(toLegalHolidayRequest(values), {
      onError: notification.error,
      onSuccess: saved => {
        notification.success();
        onSaved(saved);
        onClose();
      },
    });

  return (
    <LegalHolidayForm
      title={t('legalHolidays.form.title.create')}
      onClose={onClose}
      schema={legalHolidaySchema}
      defaultValues={defaultLegalHolidayValues}
      values={undefined}
      isSubmitting={isPending}
      isError={isError}
      isSuccess={isSuccess}
      onSubmit={onSubmit}
      onCancel={onClose}
    />
  );
}

interface LegalHolidayEditProps {
  legalHoliday: LegalHoliday;
  onSaved: (saved: LegalHoliday) => void;
  onClose: () => void;
}

function LegalHolidayEdit({ legalHoliday, onSaved, onClose }: LegalHolidayEditProps) {
  const { t } = useTranslation();
  const notification = useAppNotification();
  const { mutate, isPending, isError, isSuccess } = useLegalHolidayUpdate(legalHoliday.id);

  const onSubmit = (values: LegalHolidaySchema) =>
    mutate(toLegalHolidayRequest(values), {
      onError: notification.error,
      onSuccess: saved => {
        notification.success();
        onSaved(saved);
        onClose();
      },
    });

  const values = useMemo(() => toLegalHolidaySchema(legalHoliday), [legalHoliday]);

  return (
    <LegalHolidayForm
      title={t('legalHolidays.form.title.edit')}
      onClose={onClose}
      schema={legalHolidaySchema}
      defaultValues={defaultLegalHolidayValues}
      values={values}
      isSubmitting={isPending}
      isError={isError}
      isSuccess={isSuccess}
      onSubmit={onSubmit}
      onCancel={onClose}
    />
  );
}
